import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, Monitor, Moon, Sun, Trash2 } from "lucide-react";
import {
  useProfile,
  useRemoveProfileAvatar,
  useUpdateProfile,
  useUploadProfileAvatar,
} from "@/hooks/useApi";
import type { ProfilePatch } from "@/types";
import { cn } from "@/cn";
import { UserAvatar } from "@/components/user-avatar";
import {
  applyTheme,
  getCurrentTheme,
  onThemeChange,
  type ThemeMode,
} from "@/lib/theme";
import {
  SettingsField,
  SettingsInput,
  SettingsPageHeader,
  SettingsSection,
  SettingsTextarea,
} from "@/components/settings/settings-ui";

const THEME_OPTIONS: Array<{
  mode: ThemeMode;
  label: string;
  icon: typeof Sun;
}> = [
  { mode: "light", label: "Light", icon: Sun },
  { mode: "dark", label: "Dark", icon: Moon },
  { mode: "system", label: "System", icon: Monitor },
];

function ThemeSetting() {
  const [theme, setTheme] = useState<ThemeMode>("system");

  useEffect(() => {
    setTheme(getCurrentTheme());
    return onThemeChange(() => setTheme(getCurrentTheme()));
  }, []);

  return (
    <div className="grid max-w-md grid-cols-3 gap-2">
      {THEME_OPTIONS.map((option) => {
        const active = option.mode === theme;
        const Icon = option.icon;
        return (
          <button
            key={option.mode}
            type="button"
            aria-pressed={active}
            onClick={() => applyTheme(option.mode)}
            className={cn(
              "flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium transition-colors",
              active
                ? "border-brand bg-brand-soft text-brand"
                : "border-border bg-bg text-muted-foreground hover:bg-surface-hover hover:text-foreground",
            )}
          >
            <Icon className="size-4" />
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function splitName(full?: string): { first: string; last: string } {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

export function SettingsProfilePage() {
  const { data: profile, isLoading, error } = useProfile();
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadProfileAvatar();
  const removeAvatar = useRemoveProfileAvatar();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [assistantMemory, setAssistantMemory] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [avatarError, setAvatarError] = useState("");

  useEffect(() => {
    if (!profile || initialized) return;
    const { first, last } = splitName(profile.name);
    setFirstName(first);
    setLastName(last);
    setAssistantMemory(profile.assistantMemory ?? "");
    setInitialized(true);
  }, [profile, initialized]);

  const hasChanges = useMemo(() => {
    if (!initialized || !profile) return false;
    const name = [firstName, lastName].filter(Boolean).join(" ");
    return (
      name !== (profile.name ?? "") ||
      assistantMemory !== (profile.assistantMemory ?? "")
    );
  }, [initialized, profile, firstName, lastName, assistantMemory]);

  async function handleSave() {
    const name = [firstName, lastName].filter(Boolean).join(" ");
    const patch: ProfilePatch = { name, assistantMemory };
    await updateProfile.mutateAsync(patch);
  }

  async function handleAvatarChange(file?: File) {
    if (!file) return;
    setAvatarError("");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setAvatarError("Choose a JPEG, PNG, or WebP image.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setAvatarError("Image must be 3 MB or smaller.");
      return;
    }

    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          typeof reader.result === "string"
            ? resolve(reader.result)
            : reject(new Error("Failed to read image"));
        reader.onerror = () => reject(reader.error ?? new Error("Failed to read image"));
        reader.readAsDataURL(file);
      });
      await uploadAvatar.mutateAsync(dataUrl);
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : "Failed to upload image.");
    } finally {
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  }

  async function handleRemoveAvatar() {
    setAvatarError("");
    try {
      await removeAvatar.mutateAsync();
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : "Failed to remove image.");
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-destructive">Failed to load profile. Please try again later.</p>
    );
  }

  return (
    <div>
      <SettingsPageHeader
        title="Profile"
        onSave={() => void handleSave()}
        saving={updateProfile.isPending}
        saveDisabled={!hasChanges}
      />

      <SettingsSection title="Account">
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <UserAvatar
            name={profile?.name}
            email={profile?.email}
            src={profile?.avatarUrl}
            className="size-24 text-xl"
          />
          <div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadAvatar.isPending || removeAvatar.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                <Camera className="size-4" />
                {uploadAvatar.isPending ? "Uploading…" : "Upload image"}
              </button>
              {profile?.avatarUrl && (
                <button
                  type="button"
                  onClick={() => void handleRemoveAvatar()}
                  disabled={uploadAvatar.isPending || removeAvatar.isPending}
                  className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-surface-hover hover:text-foreground disabled:opacity-50"
                >
                  <Trash2 className="size-4" />
                  {removeAvatar.isPending ? "Removing…" : "Remove"}
                </button>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              JPEG, PNG, or WebP. Maximum 3 MB.
            </p>
            {avatarError && (
              <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
                {avatarError}
              </p>
            )}
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              onChange={(event) => void handleAvatarChange(event.target.files?.[0])}
            />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <SettingsField label="First name">
            <SettingsInput value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </SettingsField>
          <SettingsField label="Last name">
            <SettingsInput value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </SettingsField>
          <SettingsField label="Login email">
            <SettingsInput value={profile?.email ?? ""} disabled />
          </SettingsField>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Appearance"
        description="Choose how CEO Command looks on this device."
      >
        <SettingsField label="Theme">
          <ThemeSetting />
        </SettingsField>
      </SettingsSection>

      <SettingsSection
        title="Assistant memory"
        description="Persistent context the AI assistant can reference across threads."
      >
        <SettingsField label="Memory">
          <SettingsTextarea
            rows={5}
            value={assistantMemory}
            onChange={(e) => setAssistantMemory(e.target.value)}
            placeholder="Key facts, preferences, and standing instructions for your assistant…"
          />
        </SettingsField>
      </SettingsSection>
    </div>
  );
}
